import { ActionOrigin, AnalyticsService } from 'base/analytics';
import { keyOfObject } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { CompiledScheme } from 'base/styleguide/types';
import { formatError } from 'base/ui/form';
import { useRightClickMenu } from 'base/ui/menu';
import { menuDivider, MenuModel } from 'base/ui/menu_model';
import { ToastQueue } from 'base/ui/toast';
import { TextRegularLight } from 'base/ui/typography';
import { extractTreeByName } from 'base/webflow/data_attributes';
import { getClassIdToNameMap } from 'base/webflow/helpers';
import { isReplaceableTextNode } from 'base/webflow/replaceable_content';
import { Component, ComponentStyle, WfAsset } from 'base/webflow/schema';
import { styleGuide, styleGuideStyles } from 'base/webflow/style_guide';
import {
  approxSiteStyleToComponent,
  componentStyleTypeclass,
  mergeStyles,
} from 'base/webflow/style_util';
import { ProjectEditingActions } from 'blocks/editor/editing_actions/project';
import {
  ElementInstance,
  ElementInstanceState,
  MarginState,
  SectionRef,
  SectionRefState,
} from 'blocks/editor/model';
import { DragHandleOpts } from 'blocks/editor/selection/frame';
import { globalSelectionsMeasurer } from 'blocks/editor/selection/measurer';
import { useSelectable } from 'blocks/editor/selection/selectable';
import { idForSelectionState } from 'blocks/editor/selection/states';
import { SelectionStore } from 'blocks/editor/selection/store';
import {
  elementAskAiMenu,
  ElementSelectionActionsImpl,
  SelectionActions,
  selectionActionsMenu,
} from 'blocks/editor/selection_actions';
import { ProjectSession } from 'blocks/editor/session/session';
import {
  ConceptAPI,
  getGlobalConceptsStore,
} from 'blocks/editor/styleguide/concept_store';
import { RenderedImage } from 'blocks/editor/styleguide/images';
import { getImageDecorator } from 'blocks/editor/styleguide/image_styles';
import {
  StyledElementsContext,
  StyledElementView,
} from 'blocks/editor/styleguide/styled_element_view';
import { getUiStyleDecorator } from 'blocks/editor/styleguide/ui_styles';
import { globalSuggestionsStore } from 'blocks/editor/suggested_edits';
import { BREAKPOINT_TO_CANVAS_SCALE } from 'blocks/editor/wireframe/breakpoint';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { shortcut, useKeyboardShortcuts } from 'blocks/keyboard_shortcuts';
import classNames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { computedFn, expr } from 'mobx-utils';
import React from 'react';
import { BoundsMeasurer } from './bounds';
import {
  AnyNodeView,
  ComponentContainer,
  removeDeleteSuffixedNodes,
  useRootClassName,
} from './component_renderer';
import {
  baseDecorator,
  BreakpointContext,
  ComponentContext,
  composeDecorators,
  makeDropdownDecorator,
  NodeDecorator,
} from './decorators';
import styles from './element_view.css';
import {
  applyRelumeStyleHacks,
  shouldComponentUseContainerBounds,
} from './hacks';
import {
  flattenInteractables,
  getInteractablesTree,
  Interactable,
} from './interactable';
import { isNavbarWithDropdowns } from './navbars';
import { NodeMeasurerStore } from './node_measuring';
import { getElementResetMenuItem, getSlotInfoAtNode } from './slots';
import {
  elementDndDomain,
  getItemsForRendering,
  SlotDropTargetOverlay,
  SlotItemDraggable,
} from './slots_dnd';
import { MarginOverride, marginOverrideToMarginState } from './slots_margins';
import {
  getSlotItemAddButton,
  getSlotItemAddingMenuItems,
} from './slot_adding';
import { getSlotHighlightDecorator } from './slot_highligher';
import {
  ComponentCss,
  getMarginClassNameReact,
  isNodeAlternate,
  VariableDefaultsCss,
} from './styles';
import {
  getResetCopyMenuItem,
  getTextOverrideOrSuggestion,
  getTextRewritingSelectionAction,
  makeReadonlyTextOverridesDecorator,
  makeSelectToEditTextDecorator,
  SectionTextEditingModel,
  startEditingText,
  TextNodeEditingOverlay,
} from './text_overrides';

interface ComponentStyling {
  /**
   * A copy of the original component with any additional changes applied to
   * accomondate style guide builder styles. This should be used for rendering.
   */
  component: Component;
  componentStyles: ComponentStyle[];
  mergedStyles: {
    styles: ComponentStyle[];
    idUpdates: Map<string, string>;
  };
  componentAssets: WfAsset[];
  classNameMap: Map<string, string>;
}

const componentStylingCache = new Map<string, ComponentStyling>();

function getComponentStyling(
  sectionSlug: string,
  component: Component,
): ComponentStyling {
  const cachedComponentStyling = componentStylingCache.get(sectionSlug);
  if (cachedComponentStyling != null) {
    return cachedComponentStyling;
  }

  const { payload } = component;
  const componentStyles = [...styleGuideStyles, ...payload.styles];
  const mergedStyles = mergeStyles(
    styleGuide.styles.blocks.map(approxSiteStyleToComponent),
    applyRelumeStyleHacks(sectionSlug, componentStyles),
    componentStyleTypeclass,
  );
  const componentAssets = [...payload.assets, ...styleGuide.files];
  const classNameMap = getClassIdToNameMap([
    ...styleGuideStyles,
    ...componentStyles,
  ]);
  const result = {
    component,
    componentStyles,
    mergedStyles,
    componentAssets,
    classNameMap,
  };
  componentStylingCache.set(sectionSlug, result);
  return result;
}

/**
 * Renders an editable ElementInstance tree
 * (i.e. applies slotOverrides & textOverrides, adds editing/dnd overlays)
 **/
export const ElementView = observer(function _ElementView({
  sref,
  rootElement,
  library,
  selection,
  isRootSelected,
  onSelect,
  selectionActions,
  textEditing,
  outerWidth,
  innerWidth,
  innerViewHeight,
  visibilityDecorators,
  flags,
  concept,
  scheme,
  breakpoints,
  isDragPreview,
  isParentAlternate = false,
  omitStyleGuide,
}: {
  sref: SectionRef | SectionRefState;
  rootElement: ElementInstance | ElementInstanceState;
  library: LibraryCache;
  selection: SelectionStore;
  isRootSelected: boolean;
  onSelect: (slotType: 'slot-item' | 'other') => void;
  selectionActions: SelectionActions;
  textEditing: SectionTextEditingModel | undefined;
  outerWidth: number;
  innerWidth: number;
  innerViewHeight: number;
  visibilityDecorators: readonly NodeDecorator[];
  flags: FeatureFlagsStore;
  concept?: ConceptAPI;
  scheme?: CompiledScheme;
  breakpoints: BreakpointContext;
  isDragPreview?: boolean;
  isParentAlternate?: boolean;
  omitStyleGuide?: true;
}) {
  const rootClassName = useRootClassName();

  const [bounds] = React.useState(() => new BoundsMeasurer());
  bounds.ingestStyles(styleGuideStyles);

  const measuring = React.useMemo(
    () => new NodeMeasurerStore(bounds),
    [bounds],
  );
  React.useEffect(() => () => measuring.dispose(), [measuring]);

  const interactablesTree = rootElement.mutable
    ? getInteractablesTree(rootElement, library)
    : [];
  const interactables = interactablesTree.length
    ? flattenInteractables(interactablesTree)
    : [];

  useKeyboardShortcuts(isRootSelected, {
    [shortcut('Enter')]: () => {
      const child = interactables[0];
      if (child && sref.mutable) {
        selection.set({
          ...selection.factory.forSection(sref),
          element: child.element,
          nodeId: child.nodeId,
        });
      }
    },
  });

  const isSelfAlternate =
    (rootElement.mutable && globalAlternateElementStore.has(rootElement)) ||
    false;

  const isAlternate = isParentAlternate || isSelfAlternate;

  const overlays = (
    <>
      {sref.mutable && rootElement.mutable && selectionActions.editing && (
        <SlotDropTargetOverlay
          sref={sref}
          rootElement={rootElement}
          interactables={interactables}
          measuring={measuring}
          library={library}
          selection={selection}
          analytics={selectionActions.analytics}
        />
      )}
      {sref.mutable &&
        rootElement.mutable &&
        interactables
          .filter((i) => i.slotItem || (i.text && textEditing))
          .map((inter) => {
            const replacement =
              globalSuggestionsStore.getSuggestedElementReplacement(
                inter.element,
              );

            const box = (
              inter.nodeId
                ? measuring.get(inter.element, inter.nodeId)
                : measuring.getRootNode(inter.element)
            )?.box;

            const dropPreviewDimensions:
              | { width: number; height: number }
              | undefined = measuring.getRootNode('drop-preview')?.box ?? box;

            const renderDragPreview = () => {
              if (dropPreviewDimensions == null) {
                return null;
              }

              const dragPreviewProps = {
                rootElement: inter.element.state,
                sref,
                library,
                selection,
                isRootSelected,
                onSelect: () => {},
                selectionActions,
                textEditing: undefined,
                outerWidth: dropPreviewDimensions.width,
                innerWidth:
                  dropPreviewDimensions.width / BREAKPOINT_TO_CANVAS_SCALE,
                innerViewHeight:
                  dropPreviewDimensions.height / BREAKPOINT_TO_CANVAS_SCALE,
                visibilityDecorators,
                flags,
                breakpoints,
                isDragPreview: true,
              };

              if (concept != null && scheme != null) {
                return (
                  <StyledElementsContext concept={concept} library={library}>
                    <StyledElementView
                      {...dragPreviewProps}
                      concept={concept}
                      scheme={scheme}
                      isParentAlternate={isAlternate}
                    />
                  </StyledElementsContext>
                );
              }

              return (
                <ElementView
                  {...dragPreviewProps}
                  isParentAlternate={isAlternate}
                />
              );
            };

            return (
              <InteractableSelectableOverlay
                key={`iso-${inter.element.oid}/${inter.nodeId || 'root'}`}
                interactable={inter}
                textEditing={textEditing}
                selectionActions={selectionActions}
                selection={selection}
                onSelect={onSelect}
                sref={sref}
                interactableBox={box}
                flags={flags}
              >
                <SlotItemDraggable
                  enable={!!selectionActions.editing}
                  interactable={inter}
                  sref={sref}
                  rootElement={rootElement}
                  library={library}
                  renderDragPreview={renderDragPreview}
                >
                  {inter.text && textEditing && box && !replacement && (
                    <TextNodeEditingOverlay
                      interactable={inter}
                      outerBox={box}
                      action={inter.text}
                      model={textEditing}
                      measuring={measuring}
                    />
                  )}
                </SlotItemDraggable>
              </InteractableSelectableOverlay>
            );
          })}
    </>
  );

  return (
    <ComponentContainer
      rootClassName={classNames(
        rootClassName,
        styles.collapseEmptySlotWrappers,
        isDragPreview && styles.isDragPreview,
        isAlternate && styles.isAlternate,
      )}
      bounds={bounds}
      outerWidth={outerWidth}
      innerWidth={innerWidth}
      innerViewHeight={innerViewHeight}
      overlays={overlays}
      breakpoints={breakpoints}
      useContainerBounds={shouldComponentUseContainerBounds(
        rootElement.source.slug,
      )}
    >
      <InnerElementView
        element={rootElement}
        isParentAlternate={isAlternate}
        isRootElement
        {...{
          rootClassName,
          visibilityDecorators,
          library,
          measuring,
          breakpoints,
          bounds,
          selection,
          textEditing,
          interactables,
          concept,
          scheme,
          flags,
        }}
      />
      <VariableDefaultsCss
        rootClassName={rootClassName}
        variables={omitStyleGuide ? [] : styleGuide.variables || []}
      />
    </ComponentContainer>
  );
});

const BOTTOM_DRAG_HANDLE_OPTS: DragHandleOpts = {
  domain: elementDndDomain,
  draggablePosition: 'child',
  handlePosition: 'bottom',
};
const TOP_DRAG_HANDLE_OPTS: DragHandleOpts = {
  ...BOTTOM_DRAG_HANDLE_OPTS,
  handlePosition: 'top',
};
const LEFT_DRAG_HANDLE_OPTS: DragHandleOpts = {
  ...BOTTOM_DRAG_HANDLE_OPTS,
  handlePosition: 'left',
};

const getElementSelectionActions = computedFn(
  (
    selection: SelectionStore,
    section: SectionRef,
    element: ElementInstance,
    nodeId: string | undefined,
    toastQueue: ToastQueue,
    editing: ProjectEditingActions | undefined,
    analytics: AnalyticsService,
    library: LibraryCache,
    session: ProjectSession,
  ) => {
    return new ElementSelectionActionsImpl(
      selection,
      {
        ...selection.factory.forSection(section),
        element,
        nodeId,
      },
      toastQueue,
      editing,
      analytics,
      library,
      session,
    );
  },
);

const InteractableSelectableOverlay = observer(function _ISO({
  interactable,
  textEditing,
  selectionActions,
  selection,
  onSelect,
  sref,
  interactableBox,
  children,
  flags,
}: {
  interactable: Interactable;
  textEditing: SectionTextEditingModel | undefined;
  selectionActions: SelectionActions;
  selection: SelectionStore;
  onSelect: (slotType: 'slot-item' | 'other') => void;
  sref: SectionRef;
  interactableBox: DOMRect | undefined;
  children: React.ReactNode;
  flags: FeatureFlagsStore;
}) {
  const ref = React.useRef<HTMLDivElement>(null);

  const sel = {
    ...selection.factory.forSection(sref),
    element: interactable.element,
    nodeId: interactable.nodeId,
  };
  const selId = idForSelectionState(sel);
  const editing = selectionActions.editing?.forElementNode(sel);

  const color = sref.type === 'reference' ? 'global' : undefined;

  const hasDropdowns = isNavbarWithDropdowns(interactable.element.source.slug);

  const [isSelected, className] = useSelectable(ref, selection, sel, {
    frameColor: color,
    onSelect: () => onSelect(interactable.slotItem ? 'slot-item' : 'other'),
    actions: {
      textRewriting: textEditing
        ? getTextRewritingSelectionAction(textEditing, interactable)
        : undefined,
      addBefore:
        interactable.slotItem && editing
          ? getSlotItemAddButton(editing, 'before', color)
          : undefined,
      addAfter:
        interactable.slotItem && editing
          ? getSlotItemAddButton(editing, 'after', color)
          : undefined,
    },
    dragHandle: interactable.slotItem
      ? interactable.slotItem.dragHandlePosition === 'left'
        ? LEFT_DRAG_HANDLE_OPTS
        : hasDropdowns
        ? TOP_DRAG_HANDLE_OPTS
        : BOTTOM_DRAG_HANDLE_OPTS
      : undefined,
    disableHoverExtension: hasDropdowns,
  });

  // tell the global selections measurer whenever we move
  React.useEffect(() => {
    interactableBox?.x;
    interactableBox?.y;

    globalSelectionsMeasurer.invalidate(selId);
  }, [selId, interactableBox?.x, interactableBox?.y]);

  const elementSelectionActions =
    selectionActions.toastQueue &&
    selectionActions.analytics &&
    selectionActions.library &&
    selectionActions.session
      ? getElementSelectionActions(
          selection,
          sref,
          interactable.element,
          interactable.nodeId,
          selectionActions.toastQueue,
          selectionActions.editing,
          selectionActions.analytics,
          selectionActions.library,
          selectionActions.session,
        )
      : undefined;

  const menu: MenuModel = editing
    ? [
        ...(elementSelectionActions
          ? [
              ...selectionActionsMenu(
                elementSelectionActions,
                elementAskAiMenu(
                  selectionActions.editing,
                  sref,
                  interactable.element,
                  interactable.nodeId,
                ),
              ),
              menuDivider(),
            ]
          : []),
        ...(interactable.slotItem && editing
          ? getSlotItemAddingMenuItems(editing)
          : []),
        menuDivider(),
        ...(editing
          ? getElementResetMenuItem(interactable, editing)
          : textEditing
          ? getResetCopyMenuItem(interactable, textEditing)
          : []),
      ].filter(
        (x, i, a) =>
          // only keep dividers which have an item afterwards
          x.type !== 'divider' ||
          (a[i + 1] != null && a[i + 1].type !== 'divider'),
      )
    : [];
  const [onContextMenu, contextMenuPortal] = useRightClickMenu(menu);

  useKeyboardShortcuts(isSelected, {
    [shortcut('Enter')]: () => {
      if (interactable.text && textEditing) {
        startEditingText(interactable, textEditing);
        return;
      }
      const child = interactable.children?.[0];
      if (child) {
        selection.set({
          ...sel,
          element: child.element,
          nodeId: child.nodeId,
        });
      }
    },
    [shortcut('ArrowUp')]: () =>
      editing?.moveInDirection?.('up', ActionOrigin.Keyboard),
    [shortcut('ArrowUp', { shift: true })]: () =>
      editing?.moveInDirection?.('up', ActionOrigin.Keyboard),
    [shortcut('ArrowDown')]: () =>
      editing?.moveInDirection?.('down', ActionOrigin.Keyboard),
    [shortcut('ArrowDown', { shift: true })]: () =>
      editing?.moveInDirection?.('down', ActionOrigin.Keyboard),
    [shortcut('ArrowLeft')]: () =>
      editing?.moveInDirection?.('left', ActionOrigin.Keyboard),
    [shortcut('ArrowLeft', { shift: true })]: () =>
      editing?.moveInDirection?.('left', ActionOrigin.Keyboard),
    [shortcut('ArrowRight')]: () =>
      editing?.moveInDirection?.('right', ActionOrigin.Keyboard),
    [shortcut('ArrowRight', { shift: true })]: () =>
      editing?.moveInDirection?.('right', ActionOrigin.Keyboard),
  });

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        ...(interactableBox && {
          // transform is does sub-pixel positioning correctly, whereas top/left round?
          transform: `translate(${interactableBox.left}px, ${interactableBox.top}px)`,
          width: interactableBox.width,
          height: interactableBox.height,
        }),
      }}
      className={className}
      onContextMenu={onContextMenu}
    >
      {children}
      {contextMenuPortal}
    </div>
  );
});

type InnerElementViewProps = {
  element: ElementInstance | ElementInstanceState;
  originalElement?: ElementInstance;
  rootClassName: string;
  library: LibraryCache;
  measuring: NodeMeasurerStore;
  visibilityDecorators: readonly NodeDecorator[];
  breakpoints: BreakpointContext;
  bounds: BoundsMeasurer;
  selection: SelectionStore;
  textEditing: SectionTextEditingModel | undefined;
  interactables: readonly Interactable[];
  isSuggestion?: boolean;
  isDropPreview?: boolean;
  marginOverride?: MarginOverride;
  isParentAlternate: boolean;
  flags: FeatureFlagsStore;
  concept?: ConceptAPI;
  scheme?: CompiledScheme;
  isRootElement: boolean;
};

type ElementData =
  | {
      status: 'fulfilled';
      result: {
        component: Component;
        logo: { lightUrl?: RenderedImage; darkUrl?: RenderedImage };
      };
    }
  | { status: 'rejected'; error?: unknown }
  | { status: 'pending' };

const loadElementData = computedFn(
  (
    library: LibraryCache,
    element: ElementInstance | ElementInstanceState,
    useStyleGuide?: boolean,
  ): ElementData => {
    const componentJsonRequest = library.getCachedPublicComponentJson(
      element.source.slug,
      {
        fetchIfMissing: true,
      },
    )!;
    const lightLogoRequest = useStyleGuide
      ? getGlobalConceptsStore().getRenderedLogo('light')
      : undefined;
    const darkLogoRequest = useStyleGuide
      ? getGlobalConceptsStore().getRenderedLogo('dark')
      : undefined;
    const statuses = [
      componentJsonRequest.type,
      lightLogoRequest?.type,
      darkLogoRequest?.type,
    ].filter((s) => s !== undefined) as string[];

    if (statuses.includes('pending')) {
      return { status: 'pending' };
    }
    if (statuses.every((s) => s === 'fulfilled')) {
      return {
        status: 'fulfilled',
        result: {
          component: componentJsonRequest.value!,
          logo: {
            lightUrl: lightLogoRequest?.value ?? undefined,
            darkUrl: darkLogoRequest?.value ?? undefined,
          },
        },
      };
    }

    let error = undefined;
    if (componentJsonRequest.type === 'rejected') {
      error = componentJsonRequest.error;
    }
    return {
      status: 'rejected',
      error,
    };
  },
);

const InnerElementView = observer(function _InnerElementView(
  props: InnerElementViewProps,
) {
  const { element, library, concept } = props;
  const request = loadElementData(library, element, concept != null);

  if (request.status === 'pending') {
    // If the user recently added the component via the add menu, the metadata
    // and image should be in our cache.
    const meta = library.getCachedPublicComponent(element.source.slug)?.value;
    return (
      <div
        className={styles.placeholder}
        style={{
          backgroundImage: meta ? `url(${meta.thumbnailUrl})` : '',
          aspectRatio: meta
            ? `${meta.thumbnailWidth}/${meta?.thumbnailHeight}`
            : '2/1',
        }}
      />
    );
  } else if (request.status === 'rejected') {
    return (
      <div className={styles.rejected}>
        <TextRegularLight>{formatError(request.error)}</TextRegularLight>
        <TextRegularLight>Please reload the page to try again</TextRegularLight>
      </div>
    );
  }
  // Compute styling here so we can cache it. Generating the styles is a cpu
  // intensive operation we want to avoid whenever possible.
  let component = request.result.component;

  const styling = getComponentStyling(element.source.slug, component);

  if (concept) {
    return (
      <>
        <FulfilledInnerElementView
          {...props}
          component={component}
          styling={styling}
          concept={concept}
        />
      </>
    );
  }

  return (
    <FulfilledInnerElementView
      {...props}
      styling={styling}
      component={component}
    />
  );
});

const FulfilledInnerElementView = observer(function _FulfilledInnerElementView({
  component,
  element,
  originalElement,
  isSuggestion,
  isDropPreview,
  styling,
  isParentAlternate,
  isRootElement,
  ...passthrough
}: InnerElementViewProps & {
  component: Component;
  styling: ComponentStyling;
  concept?: ConceptAPI;
}) {
  const {
    rootClassName,
    visibilityDecorators,
    measuring,
    breakpoints,
    bounds,
    selection,
    textEditing,
    interactables,
    marginOverride,
    library,
    flags,
    concept,
    scheme,
  } = passthrough;
  const { componentAssets, componentStyles, mergedStyles, classNameMap } =
    styling;

  const rootNode = removeDeleteSuffixedNodes(
    extractTreeByName(component, element.source.rootName),
    componentStyles,
  );

  const isSelfAlternate = React.useMemo(
    () =>
      scheme?.alternate.has('form-input') ||
      isNodeAlternate(rootNode, classNameMap),
    [classNameMap, rootNode, scheme],
  );

  React.useEffect(() => {
    if (element.mutable && isSelfAlternate) {
      globalAlternateElementStore.add(element);
    }
    return () => {
      if (element.mutable) {
        globalAlternateElementStore.delete(element);
      }
    };
  }, [element, isSelfAlternate]);

  const isAlternate = isSelfAlternate || isParentAlternate;

  const isSelected = expr(() => {
    let sel = selection.get();
    while (sel.element) {
      if (sel.element === element) return true;
      sel = selection.factory.parentOfElementNode(sel, library);
    }
    return false;
  });

  const decorator = React.useMemo(
    () =>
      composeDecorators(
        baseDecorator,
        ...visibilityDecorators,
        makeReadonlyTextOverridesDecorator(
          (nodeId) => getTextOverrideOrSuggestion(element, nodeId),
          (nodeId) =>
            element.mutable
              ? selection.decorations.isComponentCopyPending(element, nodeId)
              : false,
        ),
        element.mutable && textEditing
          ? makeSelectToEditTextDecorator(
              textEditing,
              measuring,
              element,
              interactables,
            )
          : undefined,
        isSelected &&
          element.mutable &&
          isNavbarWithDropdowns(element.source.slug)
          ? makeDropdownDecorator({ openMenus: true })
          : undefined,
        function* (node) {
          const slotInfo = getSlotInfoAtNode(node, element.source, component);
          const slotOverride = slotInfo
            ? element.slotOverrides.get(slotInfo.name)
            : undefined;
          if (slotInfo && slotOverride) {
            if (slotInfo.mechanism === 'self') {
              yield { className: [styles.slotSelfReplaced] };
            }
            const items = getItemsForRendering(slotOverride, slotInfo, library);
            yield {
              className: [items.length === 0 && styles.emptySlot],
              wrapChildren: () => (
                <>
                  {items.map(({ item, ...opts }) => (
                    <InnerElementView
                      {...passthrough}
                      element={
                        opts.type === 'replacementSuggestion'
                          ? opts.replacement
                          : item
                      }
                      originalElement={
                        opts.type === 'replacementSuggestion' && item.mutable
                          ? item
                          : undefined
                      }
                      key={keyOfObject(item)}
                      isSuggestion={
                        opts.type === 'addSuggestion' ||
                        opts.type === 'replacementSuggestion'
                      }
                      isDropPreview={opts.type === 'dropPreview'}
                      marginOverride={opts.marginOverride}
                      isParentAlternate={isAlternate}
                      isRootElement={false}
                    />
                  ))}
                </>
              ),
            };
            // This element needs to be measured for the drop target UI
            if (element.mutable) {
              yield {
                addEffect: measuring.measureEffect(element),
              };
            }
          }

          // measuring is required to make these nodes interactive
          if (isReplaceableTextNode(node) && element.mutable) {
            yield {
              addEffect: measuring.measureEffect(element),
            };
          }

          // If this is the root node, use the measure effect specifically for
          // the root node. Unlike the regular effect, this one is agnostic to
          // node IDs, so it'll work when swapping an element for a different
          // variant.
          // We want selection frames around the root node to automatically
          // resize when swapping the element for a different variant, so they
          // can't be based on node IDs, because each variant will have a
          // different set of IDs.
          if (node._id === rootNode._id) {
            if (element.mutable) {
              yield {
                addEffect: measuring.measureEffectForRootNode(element),
              };
            } else if (originalElement) {
              yield {
                addEffect: measuring.measureEffectForRootNode(originalElement),
              };
            } else if (isDropPreview) {
              yield {
                addEffect: measuring.measureEffectForRootNode('drop-preview'),
              };
            }
          }

          if (node._id === rootNode._id && isSuggestion) {
            yield { className: [styles.isSuggestion] };
          }

          if (node._id === rootNode._id && isDropPreview) {
            yield {
              className: [
                styles.isDropPreview,
                ...(isAlternate ? [styles.isAlternate] : []),
              ],
            };
          }
        },
        concept != null ? getImageDecorator(element) : undefined,
        scheme != null ? getUiStyleDecorator(scheme) : undefined,
        flags.get('enableReactExportDebugging')
          ? getSlotHighlightDecorator({ element, component })
          : undefined,
      ),
    [
      visibilityDecorators,
      element,
      textEditing,
      measuring,
      interactables,
      isSelected,
      concept,
      scheme,
      flags,
      component,
      selection.decorations,
      rootNode._id,
      isSuggestion,
      isDropPreview,
      library,
      passthrough,
      isAlternate,
      originalElement,
    ],
  );

  const ctx = React.useMemo(
    (): ComponentContext => ({
      classNames: classNameMap,
      ix2: component.payload.ix2,
      breakpoints,
      bounds,
    }),
    [component, breakpoints, bounds, classNameMap],
  );

  const updatedStyles = mergedStyles.styles;
  bounds.ingestStyles(updatedStyles);
  const margin =
    element.mutable && globalMarginOverridesStore.has(element)
      ? globalMarginOverridesStore.get(element)
      : marginOverride
      ? marginOverrideToMarginState(marginOverride)
      : element.margin;

  return (
    <>
      <MarginWrapper margin={margin}>
        <AnyNodeView decorator={decorator} node={rootNode} ctx={ctx} />
      </MarginWrapper>
      {isRootElement && (
        <ComponentCss
          styles={updatedStyles}
          assets={componentAssets}
          rootClassName={rootClassName}
          cacheKey={`${rootClassName}-element-view`}
        />
      )}
    </>
  );
});

function MarginWrapper({
  margin,
  children,
}: {
  margin: ElementInstance['margin'];
  children: React.ReactNode;
}) {
  if (!margin) {
    return <>{children}</>;
  }
  return <div className={getMarginClassNameReact(margin)}>{children}</div>;
}

export const globalMarginOverridesStore = observable.map<
  ElementInstance,
  MarginState | undefined
>();

const globalAlternateElementStore = observable.set<ElementInstance>();
